/**
 * 로그인 모달 컴포넌트
 *
 * Ant Design Modal + Form을 사용한 로그인 폼.
 * - userId (Input), password (Input.Password) 필드
 * - Submit 시 login thunk 디스패치
 * - auth.error 상태에서 에러 메시지 표시
 * - loginModalVisible Redux 상태로 모달 표시 제어
 * - useLanguage로 다국어 지원
 */
import React, { useEffect } from 'react';
import { Modal, Form, Input, Button, Alert, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectLoginModalVisible,
  selectAuthLoading,
  selectAuthError,
  hideLoginModal,
  clearError,
  login,
} from '../../store/authSlice';
import useLanguage from '../../hooks/useLanguage';

// ─── 다국어 텍스트 ─────────────────────────────────────
const TEXTS = {
  ko: {
    title: '로그인',
    userId: '사용자 ID',
    userIdPlaceholder: '사용자 ID를 입력하세요',
    userIdRequired: '사용자 ID를 입력하세요.',
    password: '비밀번호',
    passwordPlaceholder: '비밀번호를 입력하세요',
    passwordRequired: '비밀번호를 입력하세요.',
    login: '로그인',
  },
  en: {
    title: 'Login',
    userId: 'User ID',
    userIdPlaceholder: 'Enter your User ID',
    userIdRequired: 'Please enter your User ID.',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    passwordRequired: 'Please enter your password.',
    login: 'Login',
  },
};

/**
 * 로그인 모달
 */
const LoginModal = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { t } = useLanguage(TEXTS);

  // Redux 상태
  const visible = useSelector(selectLoginModalVisible);
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);

  // 모달 닫힐 때 폼 및 에러 초기화
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      dispatch(clearError());
    }
  }, [visible, form, dispatch]);

  /** 로그인 폼 제출 핸들러 */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const result = await dispatch(login({ userId: values.userId, password: values.password }));
      if (!result.error) {
        message.success(`${values.userId}님, 환영합니다.`);
      }
    } catch (info) {
      // 유효성 검사 실패 시 자동으로 에러 표시
      console.warn('[LoginModal] 유효성 검사 실패:', info);
    }
  };

  /** 모달 닫기 핸들러 */
  const handleCancel = () => {
    dispatch(hideLoginModal());
  };

  return (
    <Modal
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
      centered
      width={400}
      bodyStyle={{ padding: 0 }}
      closable
    >
      {/* 헤더 영역 */}
      <div
        style={{
          background: 'linear-gradient(135deg, #001529 0%, #003a70 100%)',
          padding: '32px 24px 24px',
          textAlign: 'center',
          borderRadius: '4px 4px 0 0',
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px',
          }}
        >
          <UserOutlined style={{ fontSize: 28, color: '#fff' }} />
        </div>
        <div style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>
          {t.title}
        </div>
      </div>

      {/* 폼 영역 */}
      <div style={{ padding: '24px 24px 20px' }}>
        {/* 에러 메시지 표시 */}
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => dispatch(clearError())}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          {/* 사용자 ID 필드 */}
          <Form.Item
            name="userId"
            label={t.userId}
            rules={[{ required: true, message: t.userIdRequired }]}
          >
            <Input
              prefix={<UserOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
              placeholder={t.userIdPlaceholder}
              size="large"
              autoFocus
            />
          </Form.Item>

          {/* 비밀번호 필드 */}
          <Form.Item
            name="password"
            label={t.password}
            rules={[{ required: true, message: t.passwordRequired }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
              placeholder={t.passwordPlaceholder}
              size="large"
            />
          </Form.Item>

          {/* 로그인 버튼 */}
          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
              style={{
                height: 44,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #1890ff, #096dd9)',
                border: 'none',
              }}
            >
              {t.login}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default LoginModal;
